import React, { useCallback, useEffect, useState } from "react";
import { MapPinned, Loader2, RefreshCw, Send, Check } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const TONE = {
  far: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  nearby: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  no_match: "bg-secondary text-muted-foreground",
};

const headline = (r) => {
  const km = (r.distanceM / 1000).toFixed(1);
  if (r.status === "far") return `${km} km from its listing`;
  if (r.status === "nearby") return `${r.distanceM} m off`;
  return "No listing under this name";
};

// Every pin that doesn't sit on the shop's own Google listing, worst first. A pin kilometres out
// quietly sends every shopper who taps Directions somewhere the shop isn't.
export const AdminPinHealth = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setD((await api.get("/admin/pin-health")).data); } catch { setD(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setBusy("scan");
    try {
      const { data } = await api.post("/admin/pin-health/scan");
      toast({ title: `Checked ${data.checked} of ${data.shops} shops`, description: "Nobody was emailed — use the button on a row for that." });
      await load();
    } catch { toast({ title: "Could not run that check" }); }
    finally { setBusy(""); }
  };

  const recheck = async (userId) => {
    setBusy(userId + "re");
    try { await api.post(`/admin/users/${userId}/places-match`); await load(); toast({ title: "Re-checked" }); }
    catch (e) { toast({ title: e?.response?.data?.detail || "Could not re-check that shop" }); }
    finally { setBusy(""); }
  };

  const nudge = async (userId) => {
    setBusy(userId + "em");
    try {
      const { data } = await api.post(`/admin/pin-health/nudge/${userId}`);
      toast({ title: "Shop emailed", description: `${data.label} · ${(data.distanceM / 1000).toFixed(1)} km off` });
      await load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not email that shop" }); }
    finally { setBusy(""); }
  };

  if (!d) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-pin-health">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-foreground flex items-center gap-2">
            <MapPinned size={18} className="text-[#16a34a]" /> Pin health
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Shops whose map pin doesn't sit on the Google listing under their own name, worst first.
            {" "}{d.clean} clean · {d.unchecked.length} never checked · {d.shops} shops.
          </p>
        </div>
        <button onClick={scan} disabled={!!busy} data-testid="pin-health-scan-btn"
          className="inline-flex items-center gap-2 border border-border hover:border-[#16a34a] disabled:opacity-60 text-foreground text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
          {busy === "scan" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Check every shop
        </button>
      </div>

      {d.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2" data-testid="pin-health-empty">
          <Check size={15} className="text-[#16a34a]" /> Every checked pin lines up with its listing.
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {d.rows.map((r, i) => (
            <div key={`${r.userId}-${i}`} data-testid={`pin-health-row-${r.userId}`}
              className="border border-border rounded-xl p-3 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {r.businessName}
                  <span className={`ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full ${TONE[r.status]}`}>
                    {headline(r)}
                  </span>
                  {r.cityLevel && <span className="ml-2 text-[11px] text-muted-foreground">town-wide check</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {r.label || "Pin"}
                  {r.listing && r.status !== "no_match" ? ` → ${r.listing}${r.address ? `, ${r.address}` : ""}` : ""}
                </p>
                {r.status === "no_match" && r.listing && (
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5 truncate">
                    Closest name Google returned was “{r.listing}” — not close enough to call a match.
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {r.email}
                  {r.checkedAt ? ` · checked ${new Date(r.checkedAt).toLocaleDateString()}` : ""}
                  {r.nudgedAt ? ` · emailed ${new Date(r.nudgedAt).toLocaleDateString()}` : " · never emailed"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => recheck(r.userId)} disabled={!!busy} data-testid={`pin-health-recheck-${r.userId}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] disabled:opacity-60 text-foreground px-3 py-2 rounded-xl transition-colors">
                  {busy === r.userId + "re" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Re-check
                </button>
                {r.status !== "no_match" && (
                  <button onClick={() => nudge(r.userId)} disabled={!!busy} data-testid={`pin-health-nudge-${r.userId}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-3 py-2 rounded-xl transition-colors">
                    {busy === r.userId + "em" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Email shop
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPinHealth;
